import { useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import saveAs from "file-saver";
import { toast } from "react-toastify";
import { useUploadDocument } from "../contact/useUploadDocument";
import { REPORT_DATA_QUERY, ReportDataQuery } from "./useGetReportData";

export const useDownloadReport = (reportName: string, isExcel: boolean) => {
  const [downloading, setDownloading] = useState(false);
  const { uploadDocument } = useUploadDocument();
  const contactIdRef = useRef<number | undefined>(undefined);
  const onCompletedRef = useRef<((base: string) => void) | undefined>(
    undefined
  );
  const completedCalledRef = useRef(false);

  const [performQuery] = useLazyQuery<ReportDataQuery>(REPORT_DATA_QUERY, {
    fetchPolicy: "network-only",
    onCompleted: (d) => {
      // Guard against Apollo/Strict Mode double-invocation
      if (completedCalledRef.current) return;
      completedCalledRef.current = true;

      const documentData = d.report;
      saveAs(
        `data:application/${
          isExcel
            ? "vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "pdf"
        };base64,` + documentData,
        `${reportName}`
      );
      if (contactIdRef.current) {
        uploadDocument(
          contactIdRef.current,
          reportName,
          documentData,
          "Online"
        ).then((res) => {
          setDownloading(false);
          res?.data?.uploadDocumentInContext &&
            toast.success("Report uploaded successfully", {
              autoClose: 2000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              position: "top-right",
              theme: "colored",
            });
          onCompletedRef.current?.(documentData);
        });
      } else {
        onCompletedRef.current?.(documentData);
        setDownloading(false);
      }
    },
    onError: (err) => {
      toast.error(err.message, {
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        position: "top-right",
        theme: "colored",
      });
      setDownloading(false);
    },
  });

  const [performQ] = useLazyQuery<ReportDataQuery>(REPORT_DATA_QUERY, {
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    onCompleted: (d) => {
      const documentData = d.report;
      return documentData;
    },
    onError: (err) => {
      toast.error(err.message, {
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        position: "top-right",
        theme: "colored",
      });
    },
  });

  const downloadReport = async (
    reportName: string,
    reportVariables: Record<string, unknown>,
    contactId?: number,
    onCompleted?: (base: string) => void
  ) => {
    setDownloading(true);
    contactIdRef.current = contactId;
    onCompletedRef.current = onCompleted;
    completedCalledRef.current = false;
    await performQuery({
      variables: { reportName, reportVariables },
    });
  };

  const getReportData = async (
    reportName: string,
    reportVariables: Record<string, unknown>
  ) => {
    setDownloading(true);
    const data = await performQ({
      variables: { reportName, reportVariables, _random: Math.random() },
      context: { fetchOptions: { cache: "no-store" } },
    });
    setDownloading(false);
    return data;
  };

  return { downloadReport, getReportData, downloading, setDownloading };
};
